import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import toast from "react-hot-toast";
import { Eye, EyeOff, LogIn, Calculator, Sparkles, Check, Download, Smartphone } from "lucide-react";

export default function LoginPage() {
  const [email, setEmail] = useState(localStorage.getItem("saved_email") || "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(localStorage.getItem("remember_me") === "true");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // PWA Install State
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // التحقق من iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(isIOSDevice);

    // التحقق من أن التطبيق مثبت بالفعل
    if (window.matchMedia("(display-mode: standalone)").matches) {
      setIsInstalled(true);
      return;
    }

    // الاستماع لحدث beforeinstallprompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    // الاستماع لحدث التثبيت
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
      toast.success("تم تثبيت التطبيق بنجاح! 🎉");
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstall);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (isIOS) {
      // عرض تعليمات iOS
      toast((t) => (
        <div className="text-right">
          <p className="font-bold mb-2">لتثبيت التطبيق على iPhone:</p>
          <ol className="text-sm space-y-1">
            <li>1. اضغط على أيقونة المشاركة ↑</li>
            <li>2. اختر "إضافة إلى الشاشة الرئيسية"</li>
            <li>3. اضغط "إضافة"</li>
          </ol>
        </div>
      ), { duration: 6000 });
      return;
    }

    if (!deferredPrompt) {
      // عرض تعليمات للكمبيوتر
      toast((t) => (
        <div className="text-right">
          <p className="font-bold mb-2">لتثبيت التطبيق على الكمبيوتر:</p>
          <ol className="text-sm space-y-1">
            <li>1. اضغط على أيقونة التثبيت ⊕ في شريط العنوان</li>
            <li>2. أو من القائمة ⋮ اختر "تثبيت التطبيق"</li>
          </ol>
        </div>
      ), { duration: 6000 });
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === "accepted") {
      setIsInstalled(true);
    }
    
    setDeferredPrompt(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const userData = await login(email.trim().toLowerCase(), password);
      
      // حفظ البريد إذا تم اختيار "تذكرني"
      if (rememberMe) {
        localStorage.setItem("saved_email", email);
        localStorage.setItem("remember_me", "true");
      } else {
        localStorage.removeItem("saved_email");
        localStorage.removeItem("remember_me");
      }
      
      toast.success("تم تسجيل الدخول بنجاح");
      
      // التحقق من حالة التفعيل
      if (!userData.is_active) {
        navigate("/pending-activation");
      } else if (userData.is_employee && userData.role === "cashier") {
        // ✨ وضع البساطة: الكاشير يذهب مباشرة إلى POS
        navigate("/pos");
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "خطأ في تسجيل الدخول";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      dir="rtl"
    >
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-emerald-900/20 via-transparent to-transparent" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-teal-900/20 via-transparent to-transparent" />
      
      {/* Floating Shapes */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-500/10 rounded-full blur-3xl animate-pulse" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      
      {/* Grid Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:50px_50px]" />

      <Card className="w-full max-w-md bg-slate-900/80 backdrop-blur-2xl border-white/10 shadow-2xl relative z-10">
        <CardHeader className="text-center pb-2">
          <div className="flex flex-col items-center gap-4 mb-4">
            {/* Logo */}
            <div className="relative">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-2xl shadow-emerald-500/30 transform hover:scale-105 transition-transform">
                <Calculator className="w-12 h-12 text-white" />
              </div>
              <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center shadow-lg">
                <Sparkles className="w-3 h-3 text-white" />
              </div>
            </div>
            <div>
              <CardTitle className="text-2xl font-bold text-white tracking-tight">
                نازاريو حساباتي
              </CardTitle>
              <p className="text-slate-400 text-sm mt-2">
                نظام إدارة حسابات المتاجر
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-300 text-sm font-medium">
                البريد الإلكتروني
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-slate-950/50 border-white/10 text-white placeholder:text-slate-500 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/50 h-12 rounded-xl transition-all"
                placeholder="example@email.com"
                required
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-300 text-sm font-medium">
                كلمة المرور
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-slate-950/50 border-white/10 text-white placeholder:text-slate-500 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/50 h-12 rounded-xl pe-10 transition-all"
                  placeholder="••••••••"
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* خيار تذكرني */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setRememberMe(!rememberMe)}
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                    rememberMe 
                      ? "bg-emerald-600 border-emerald-600" 
                      : "border-slate-600 hover:border-slate-500"
                  }`}
                >
                  {rememberMe && <Check className="w-3 h-3 text-white" />}
                </button>
                <span className="text-slate-400 text-sm">
                  تذكرني
                </span>
              </div>
              <Link
                to="/forgot-password"
                className="text-emerald-500 hover:text-emerald-400 text-sm font-medium transition-colors"
                data-testid="forgot-password-link"
              >
                نسيت كلمة المرور؟
              </Link>
            </div>

            <Button
              type="submit"
              className="w-full h-12 rounded-xl font-bold text-white transition-all transform hover:scale-[1.02] active:scale-[0.98] bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 shadow-lg shadow-emerald-500/25"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  جاري الدخول...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <LogIn size={18} />
                  تسجيل الدخول
                </span>
              )}
            </Button>

            <p className="text-center text-slate-400 text-sm">
              ليس لديك حساب؟{" "}
              <Link
                to="/register"
                className="text-emerald-400 hover:text-emerald-300 font-medium transition-colors"
                data-testid="register-link"
              >
                سجّل الآن
              </Link>
            </p>

            {/* Install App Button */}
            {!isInstalled && (
              <div className="mt-4 p-4 rounded-xl bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/20">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                      <Smartphone className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-blue-300 text-sm font-medium">ثبّت التطبيق</p>
                      <p className="text-slate-500 text-xs">للوصول السريع من جهازك</p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    onClick={handleInstallClick}
                    className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-2"
                    data-testid="install-app-btn"
                  >
                    <Download className="w-4 h-4 me-1" />
                    تثبيت
                  </Button>
                </div>
              </div>
            )}

            {/* Info Box */}
            <div className="mt-3 p-4 rounded-xl bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/20">
              <p className="text-emerald-400 text-sm text-center flex items-center justify-center gap-2">
                <Sparkles className="w-4 h-4" />
                سجل حساب جديد وأرسل طلب تفعيل من صفحة الدعم
              </p>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
